import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { ArrowLeft, CheckCircle, XCircle, ChevronDown, ChevronUp } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { PdfViewer } from '@/components/application/pdf-viewer/pdf-viewer';
import { Table, TableCard } from '@/components/application/table/table';
import { trainingDocumentService, documentSchemaService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import type { TrainingDocument, DocumentSchema } from '@/types/document-schema';
import { useTranslation } from 'react-i18next';

const AI_PROVIDER_LABELS: Record<string, string> = {
    anthropic: 'Anthropic (Claude)',
    openai: 'OpenAI (GPT)',
    openrouter: 'OpenRouter',
    deepseek: 'DeepSeek',
    google: 'Google (Gemini)',
    meta: 'Meta (Llama)',
    mistral: 'Mistral AI',
};

export const TrainingDocumentDetailPage = () => {
    const { trainingDocId } = useParams<{ trainingDocId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [document, setDocument] = useState<TrainingDocument | null>(null);
    const [schema, setSchema] = useState<DocumentSchema | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
    const [validatingKey, setValidatingKey] = useState<string | null>(null);
    const [isInfoExpanded, setIsInfoExpanded] = useState(true);

    useEffect(() => {
        if (trainingDocId) {
            loadDocument();
        }
    }, [trainingDocId]);

    const loadDocument = () => {
        if (!trainingDocId) return;

        setIsLoading(true);
        const doc = trainingDocumentService.getById(trainingDocId);

        if (!doc) {
            setIsLoading(false);
            return;
        }

        setDocument(doc);

        // Load schema if available
        if (doc.document_schema_id) {
            const schemaData = documentSchemaService.getById(doc.document_schema_id);
            setSchema(schemaData || null);
        }

        // Set first provider as selected if available
        if (doc.ai_providers && doc.ai_providers.length > 0) {
            setSelectedProvider(doc.ai_providers[0]);
        }

        setIsLoading(false);
    };

    const handleValidateValue = (provider: string, attributeKey: string, status: 'correct' | 'incorrect') => {
        if (!trainingDocId) return;

        const key = `${provider}-${attributeKey}-${status}`;
        setValidatingKey(key);

        // Simulate 1 second loading
        setTimeout(() => {
            const success = trainingDocumentService.validateExtractedValue(trainingDocId, provider, attributeKey, status);
            if (success) {
                loadDocument(); // Reload to show updated validation status
            }
            setValidatingKey(null);
        }, 1000);
    };

    const getValidationStatusBadge = (status?: string) => {
        switch (status) {
            case 'correct':
                return <Badge color="success" size="sm">{t('training.validationStatus.correct')}</Badge>;
            case 'incorrect':
                return <Badge color="error" size="sm">{t('training.validationStatus.incorrect')}</Badge>;
            default:
                return <Badge color="gray" size="sm">{t('training.validationStatus.pending')}</Badge>;
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <Badge color="gray" size="sm">{t('training.status.pending')}</Badge>;
            case 'processing':
                return <Badge color="blue" size="sm">{t('training.status.processing')}</Badge>;
            case 'completed':
                return <Badge color="success" size="sm">{t('training.status.completed')}</Badge>;
            case 'failed':
                return <Badge color="error" size="sm">{t('training.status.failed')}</Badge>;
            default:
                return <Badge color="gray" size="sm">{status}</Badge>;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    if (!document) {
        return (
            <div className="space-y-6">
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-primary mb-2">{t('training.detail.notFound.title')}</h3>
                    <p className="text-tertiary mb-4">{t('training.detail.notFound.message')}</p>
                    <Button onClick={() => navigate('/admin/documents/training')} size="md" color="secondary">
                        {t('training.detail.notFound.backToList')}
                    </Button>
                </div>
            </div>
        );
    }

    const currentDocType = DOCUMENT_TYPES.find((dt) => dt.key === document.document_type);

    // Use test PDF if file doesn't exist
    const displayPdfUrl = '/document-review-test.pdf';

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
                <Link
                    to="/admin/documents/training"
                    className="text-tertiary hover:text-primary"
                >
                    {t('training.breadcrumb.trainingDocuments')}
                </Link>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{document.file_name}</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{document.file_name}</h2>
                    <p className="text-tertiary mt-1">{t('training.detail.title')}</p>
                </div>
                <Button
                    onClick={() => navigate('/admin/documents/training')}
                    size="md"
                    color="secondary"
                    iconLeading={ArrowLeft}
                >
                    {t('training.detail.backToList')}
                </Button>
            </div>

            <div className="space-y-6">
                {/* Info Processo e Training - Above PDF */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <button
                        onClick={() => setIsInfoExpanded(!isInfoExpanded)}
                        className="flex items-center justify-between w-full mb-4 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                        <h3 className="text-lg font-semibold text-primary">{t('training.detail.infoTitle')}</h3>
                        {isInfoExpanded ? (
                            <ChevronUp className="size-5 text-tertiary" />
                        ) : (
                            <ChevronDown className="size-5 text-tertiary" />
                        )}
                    </button>
                    {isInfoExpanded && (
                        <div className="space-y-3 transition-all duration-200">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.documentType')}</label>
                                    <p className="text-primary">{currentDocType?.label || document.document_type}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.status')}</label>
                                    <div>{getStatusBadge(document.training_status)}</div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.dataset')}</label>
                                    <p className="text-primary">{document.dataset_name}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.datasetVersion')}</label>
                                    <p className="text-primary">{document.dataset_version}</p>
                                </div>
                                {document.document_schema_id && schema && (
                                    <div>
                                        <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.schema')}</label>
                                        <p className="text-primary">{schema.schema_name}</p>
                                    </div>
                                )}
                                {document.detected_schema && (
                                    <div>
                                        <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.detectedSchema')}</label>
                                        <p className="text-primary font-mono">{document.detected_schema}</p>
                                    </div>
                                )}
                                {document.extraction_score !== undefined && (
                                    <div>
                                        <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.extractionScore')}</label>
                                        <p className="text-primary">{(document.extraction_score * 100).toFixed(1)}%</p>
                                    </div>
                                )}
                                {document.validation_quality_score !== undefined && (
                                    <div>
                                        <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.validationScore')}</label>
                                        <p className="text-primary">{(document.validation_quality_score * 100).toFixed(1)}%</p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.createdAt')}</label>
                                    <p className="text-primary">{document.created_at.toLocaleDateString('it-IT')}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.updatedAt')}</label>
                                    <p className="text-primary">{document.updated_at.toLocaleDateString('it-IT')}</p>
                                </div>
                            </div>

                            {document.ai_providers && document.ai_providers.length > 0 && (
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.aiProviders')}</label>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {document.ai_providers.map((provider) => (
                                            <Badge key={provider} color="blue" size="sm">
                                                {AI_PROVIDER_LABELS[provider] || provider}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {document.custom_variables && Object.keys(document.custom_variables).length > 0 && (
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.customVariables')}</label>
                                    <div className="mt-1 space-y-1">
                                        {Object.entries(document.custom_variables).map(([key, value]) => (
                                            <div key={key} className="text-sm">
                                                <span className="font-medium text-primary">{key}:</span>{' '}
                                                <span className="text-tertiary">{value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {document.notes && (
                                <div>
                                    <label className="text-sm font-medium text-tertiary">{t('training.detail.infoFields.notes')}</label>
                                    <p className="text-primary">{document.notes}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Main Content Area - PDF and Extracted Data Side by Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - PDF Viewer */}
                    <div>
                        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                            <h3 className="text-lg font-semibold text-primary mb-4">{t('training.detail.documentTitle')}</h3>
                            <div className="h-[600px]">
                                <PdfViewer fileUrl={displayPdfUrl} polygons={[]} hideAddRectangle />
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Extracted Data */}
                    {document.extracted_data && Object.keys(document.extracted_data).length > 0 && (
                        <div>
                            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                <h3 className="text-lg font-semibold text-primary mb-4">{t('training.detail.extractedDataTitle')}</h3>

                                {/* Provider Tabs */}
                                <div className="flex items-center gap-2 rounded-lg bg-secondary p-1 ring-1 ring-secondary_alt mb-4">
                                    {Object.keys(document.extracted_data).map((provider) => (
                                        <Button
                                            key={provider}
                                            onClick={() => setSelectedProvider(provider)}
                                            size="sm"
                                            color={selectedProvider === provider ? 'primary' : 'secondary'}
                                        >
                                            {AI_PROVIDER_LABELS[provider] || provider}
                                        </Button>
                                    ))}
                                </div>

                                {/* Extracted Data Table */}
                                {selectedProvider && document.extracted_data[selectedProvider] && (
                                    <TableCard.Root>
                                        <Table>
                                            <Table.Header>
                                                <Table.Head label={t('training.detail.tableHeaders.attribute')} isRowHeader />
                                                <Table.Head label={t('training.detail.tableHeaders.extractedValue')} />
                                                <Table.Head label={t('training.detail.tableHeaders.confidence')} />
                                                <Table.Head label={t('training.detail.tableHeaders.validationStatus')} />
                                                <Table.Head label={t('training.detail.tableHeaders.actions')} />
                                            </Table.Header>
                                            <Table.Body>
                                                {Object.entries(document.extracted_data[selectedProvider].extracted_attributes).map(([key, attr]) => (
                                                    <Table.Row key={key}>
                                                        <Table.Cell>
                                                            <div className="font-medium text-primary">{key}</div>
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <span className="text-sm text-primary">
                                                                {typeof attr.value === 'object'
                                                                    ? JSON.stringify(attr.value)
                                                                    : String(attr.value)}
                                                            </span>
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {attr.confidence !== undefined ? (
                                                                <span className="text-sm text-primary">
                                                                    {(attr.confidence * 100).toFixed(1)}%
                                                                </span>
                                                            ) : (
                                                                <span className="text-sm text-tertiary">-</span>
                                                            )}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            {getValidationStatusBadge(attr.validation_status)}
                                                        </Table.Cell>
                                                        <Table.Cell>
                                                            <div className="flex items-center gap-2">
                                                                <Button
                                                                    onClick={() => handleValidateValue(selectedProvider, key, 'correct')}
                                                                    size="sm"
                                                                    color={attr.validation_status === 'correct' ? 'primary' : 'secondary'}
                                                                    iconLeading={validatingKey === `${selectedProvider}-${key}-correct` ? undefined : CheckCircle}
                                                                    disabled={attr.validation_status === 'correct' || validatingKey !== null}
                                                                    data-loading={validatingKey === `${selectedProvider}-${key}-correct`}
                                                                >
                                                                    {validatingKey === `${selectedProvider}-${key}-correct` ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                                                                            <span>{t('training.actions.loading')}</span>
                                                                        </div>
                                                                    ) : (
                                                                        t('training.actions.correct')
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    onClick={() => handleValidateValue(selectedProvider, key, 'incorrect')}
                                                                    size="sm"
                                                                    color={attr.validation_status === 'incorrect' ? 'primary-destructive' : 'secondary-destructive'}
                                                                    iconLeading={validatingKey === `${selectedProvider}-${key}-incorrect` ? undefined : XCircle}
                                                                    disabled={attr.validation_status === 'incorrect' || validatingKey !== null}
                                                                    data-loading={validatingKey === `${selectedProvider}-${key}-incorrect`}
                                                                >
                                                                    {validatingKey === `${selectedProvider}-${key}-incorrect` ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent" />
                                                                            <span>{t('training.actions.loading')}</span>
                                                                        </div>
                                                                    ) : (
                                                                        t('training.actions.incorrect')
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                ))}
                                            </Table.Body>
                                        </Table>
                                    </TableCard.Root>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
